
import React, { useState, useEffect, useMemo } from "react";
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Search,
  ChevronRight,
  ChevronDown,
  Calculator,
  BarChart3,
  Lightbulb,
  Loader2,
  DollarSign,
  Target,
  Users,
  Zap,
  TrendingUp,
  List,
  Check,
  Save,
  PieChart,
  ShoppingBag,
  HelpCircle
} from "lucide-react";

import { toolsService } from "../../../services/toolsService";

// ---------------------------------------------------------
// CONSTANTS & HELPERS
// ---------------------------------------------------------
const ViewState = {
  DASHBOARD: "DASHBOARD",
  EDITOR: "EDITOR",
  MODEL_VIEW: "MODEL_VIEW"
};

const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : n;

const safeNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// ---------------------------------------------------------
// SHARED UI COMPONENTS
// ---------------------------------------------------------

const StatCard = ({ icon: Icon, label, value, subValue, color = 'indigo', onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-white rounded-2xl border border-gray-100">
    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

// ---------------------------------------------------------
// STEP COMPONENTS
// ---------------------------------------------------------

const Step1Basics = ({ data, update }) => (
  <div className="max-w-2xl mx-auto space-y-6">
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
        <ShoppingBag className="w-5 h-5 text-indigo-600" />
        Product Basics
      </h2>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
        <input
          type="text"
          placeholder="e.g. Premium Coaching Package"
          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
          value={data.productData?.name || ""}
          onChange={(e) =>
            update("productData", {
              ...data.productData,
              name: e.target.value,
            })
          }
        />
      </div>
    </div>
  </div>
);

const Step2Costs = ({ data, update }) => {
  const costData = data.costData || { directCosts: [], overheads: 0, unitCost: 0 };

  const addLine = () => {
    const newLine = { id: Date.now().toString(), label: "", amount: 0 };
    const next = { ...costData, directCosts: [...(costData.directCosts || []), newLine] };
    update("costData", next);
  };

  const updateLine = (id, field, value) => {
    const next = {
      ...costData,
      directCosts: (costData.directCosts || []).map((l) =>
        l.id === id ? { ...l, [field]: field === "amount" ? safeNum(value) : value } : l
      ),
    };
    recalcUnit(next);
  };

  const removeLine = (id) => {
    const next = { ...costData, directCosts: (costData.directCosts || []).filter((l) => l.id !== id) };
    recalcUnit(next);
  };

  const recalcUnit = (next) => {
    const totalDirect = (next.directCosts || []).reduce((s, i) => s + safeNum(i.amount), 0);
    const unit = totalDirect + safeNum(next.overheads);
    const out = { ...next, unitCost: unit };
    update("costData", out);
  };

  const setOverheads = (v) => {
    const next = { ...costData, overheads: safeNum(v) };
    recalcUnit(next);
  };

  useEffect(() => {
    if (!data.costData) update("costData", costData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalDirect = (costData.directCosts || []).reduce((s, i) => s + safeNum(i.amount), 0);
  const unitCost = totalDirect + safeNum(costData.overheads);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Unit Cost Hero */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-indigo-100 font-medium mb-1">Total Unit Cost</p>
            <p className="text-4xl font-bold">R {fmt(unitCost)}</p>
          </div>
          <div className="text-right">
            <div className="bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm">
              <p className="text-xs text-indigo-100">Direct Costs</p>
              <p className="font-semibold">R {fmt(totalDirect)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <List className="w-5 h-5 text-indigo-600" />
          Cost Breakdown
        </h2>

        <div className="space-y-3">
          {(costData.directCosts || []).map((line) => (
            <div key={line.id} className="flex gap-3 items-center group">
              <input
                type="text"
                placeholder="Item Label"
                value={line.label}
                onChange={(e) => updateLine(line.id, "label", e.target.value)}
                className="flex-grow px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 outline-none"
              />
              <div className="relative w-32">
                <span className="absolute left-3 top-2.5 text-gray-400 text-sm">R</span>
                <input
                  type="number"
                  min="0"
                  value={line.amount}
                  onChange={(e) => updateLine(line.id, "amount", e.target.value)}
                  className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 outline-none text-right"
                />
              </div>
              <button 
                onClick={() => removeLine(line.id)} 
                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}

          <button 
            onClick={addLine} 
            className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 font-medium hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50 transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" /> Add Cost Item
          </button>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">Fixed Overheads</p>
              <p className="text-sm text-gray-500">Allocated per unit (e.g. rent, software)</p>
            </div>
            <div className="relative w-40">
              <span className="absolute left-3 top-2.5 text-gray-400 text-sm">R</span>
              <input
                type="number"
                min="0"
                value={costData.overheads}
                onChange={(e) => setOverheads(e.target.value)}
                className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 outline-none text-right font-medium"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Step3Value = ({ data, update }) => {
  // FIX: Ensure structure exists by merging defaults with data
  const value = {
    benefits: [],
    pains: [],
    evc: 0,
    narrative: "",
    ...data.valuePerception,
    willingnessToPay: { 
      min: 0, 
      max: 0, 
      ...(data.valuePerception?.willingnessToPay || {}) 
    }
  };

  const addBenefit = () => update("valuePerception", { ...value, benefits: [...value.benefits, { id: Date.now(), text: "" }] });
  const updateBenefit = (id, text) => update("valuePerception", { ...value, benefits: value.benefits.map((b) => (b.id === id ? { ...b, text } : b)) });
  const removeBenefit = (id) => update("valuePerception", { ...value, benefits: value.benefits.filter((b) => b.id !== id) });

  const addPain = () => update("valuePerception", { ...value, pains: [...value.pains, { id: Date.now(), text: "" }] });
  const updatePain = (id, text) => update("valuePerception", { ...value, pains: value.pains.map((p) => (p.id === id ? { ...p, text } : p)) });
  const removePain = (id) => update("valuePerception", { ...value, pains: value.pains.filter((p) => p.id !== id) });

  useEffect(() => {
    // Only initialize if the top-level object is missing
    if (!data.valuePerception) {
      update("valuePerception", { 
        benefits: [], 
        pains: [], 
        evc: 0, 
        willingnessToPay: { min: 0, max: 0 }, 
        narrative: "" 
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Benefits */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 h-full">
          <h3 className="font-semibold text-emerald-700 flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5" /> Value Drivers (Benefits)
          </h3>
          <div className="space-y-3">
            {(value.benefits || []).map((b) => (
              <div key={b.id} className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="e.g. Saves 10 hours/week"
                  value={b.text}
                  onChange={(e) => updateBenefit(b.id, e.target.value)}
                  className="flex-grow px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm"
                />
                <button onClick={() => removeBenefit(b.id)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button onClick={addBenefit} className="text-sm text-emerald-600 font-medium hover:text-emerald-700 flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add Benefit
            </button>
          </div>
        </div>

        {/* Pains */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 h-full">
          <h3 className="font-semibold text-rose-700 flex items-center gap-2 mb-4">
            <Target className="w-5 h-5" /> Pain Relievers
          </h3>
          <div className="space-y-3">
            {(value.pains || []).map((p) => (
              <div key={p.id} className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="e.g. Reduces compliance risk"
                  value={p.text}
                  onChange={(e) => updatePain(p.id, e.target.value)}
                  className="flex-grow px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500/20 outline-none text-sm"
                />
                <button onClick={() => removePain(p.id)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button onClick={addPain} className="text-sm text-rose-600 font-medium hover:text-rose-700 flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add Pain Point
            </button>
          </div>
        </div>
      </div>

      {/* Financials */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-indigo-600" />
          Financial Valuation
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Economic Value to Customer (EVC)</label>
            <div className="relative mb-2">
              <span className="absolute left-3 top-2.5 text-gray-400 text-sm">R</span>
              <input
                type="number"
                value={value.evc}
                onChange={(e) => update("valuePerception", { ...value, evc: safeNum(e.target.value) })}
                className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 outline-none"
                placeholder="0"
              />
            </div>
            <p className="text-xs text-gray-500">Total estimated money/time saved for the client.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Willingness to Pay Range</label>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <span className="absolute left-3 top-2.5 text-gray-400 text-sm">R</span>
                <input
                  type="number"
                  placeholder="Min"
                  value={value.willingnessToPay.min}
                  onChange={(e) => update("valuePerception", { ...value, willingnessToPay: { ...value.willingnessToPay, min: safeNum(e.target.value) } })}
                  className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl outline-none"
                />
              </div>
              <span className="text-gray-400">-</span>
              <div className="relative flex-1">
                <span className="absolute left-3 top-2.5 text-gray-400 text-sm">R</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={value.willingnessToPay.max}
                  onChange={(e) => update("valuePerception", { ...value, willingnessToPay: { ...value.willingnessToPay, max: safeNum(e.target.value) } })}
                  className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-xl outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Step4Competition = ({ data, update }) => {
  const comp = data.competition || { competitors: [], referencePrice: 0 };

  const addCompetitor = () => update("competition", { ...comp, competitors: [...(comp.competitors || []), { id: Date.now().toString(), name: "", price: 0, notes: "" }] });
  
  const updateCompetitor = (id, field, value) => {
    const nextList = (comp.competitors || []).map((c) => (c.id === id ? { ...c, [field]: field === "price" ? safeNum(value) : value } : c));
    update("competition", { ...comp, competitors: nextList, referencePrice: computeReferencePrice(nextList) });
  };

  const removeCompetitor = (id) => {
    const nextList = (comp.competitors || []).filter((c) => c.id !== id);
    update("competition", { ...comp, competitors: nextList, referencePrice: computeReferencePrice(nextList) });
  };

  const computeReferencePrice = (list) => {
    const prices = (list || []).map((c) => safeNum(c.price)).filter((p) => p > 0);
    return prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : 0;
  };

  useEffect(() => {
    if (!data.competition) update("competition", comp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" /> Competitors
          </h2>
          <div className="bg-indigo-50 px-4 py-2 rounded-xl border border-indigo-100">
             <span className="text-xs text-indigo-600 font-medium uppercase tracking-wider">Market Avg</span>
             <p className="text-xl font-bold text-indigo-900">R {fmt(comp.referencePrice)}</p>
          </div>
        </div>

        <div className="space-y-4">
          {(comp.competitors || []).map((c) => (
            <div key={c.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="md:col-span-4">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Name</label>
                <input
                  type="text"
                  placeholder="Competitor Name"
                  value={c.name}
                  onChange={(e) => updateCompetitor(c.id, "name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div className="md:col-span-3">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Price</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-400 text-xs">R</span>
                  <input
                    type="number"
                    value={c.price}
                    onChange={(e) => updateCompetitor(c.id, "price", e.target.value)}
                    className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="md:col-span-4">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Notes</label>
                <input
                  type="text"
                  placeholder="Strengths/Weaknesses"
                  value={c.notes}
                  onChange={(e) => updateCompetitor(c.id, "notes", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div className="md:col-span-1 pt-6 text-center">
                 <button onClick={() => removeCompetitor(c.id)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          <button 
            onClick={addCompetitor} 
            className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 font-medium hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50 transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" /> Add Competitor
          </button>
        </div>
      </div>
    </div>
  );
};

const Step5Behavioural = ({ data, update }) => {
  const behavioural = data.behaviouralPricing || { charmPricing: true, decoyEnabled: false, tiers: [] };

  const setFlag = (k, v) => update("behaviouralPricing", { ...behavioural, [k]: v });
  const addTier = () => update("behaviouralPricing", { ...behavioural, tiers: [...(behavioural.tiers || []), { id: Date.now().toString(), name: "Standard", price: 0 }] });
  const updateTier = (id, field, value) => update("behaviouralPricing", { ...behavioural, tiers: (behavioural.tiers || []).map((t) => (t.id === id ? { ...t, [field]: field === "price" ? safeNum(value) : value } : t)) });
  const removeTier = (id) => update("behaviouralPricing", { ...behavioural, tiers: (behavioural.tiers || []).filter((t) => t.id !== id) });

  useEffect(() => {
    if (!data.behaviouralPricing) update("behaviouralPricing", behavioural);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Toggles */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
           <Zap className="w-5 h-5 text-indigo-600" /> Psychology & Tiers
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${behavioural.charmPricing ? 'border-indigo-500 bg-indigo-50' : 'border-gray-100 hover:border-gray-200'}`}>
              <div className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center ${behavioural.charmPricing ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'}`}>
                {behavioural.charmPricing && <Check className="w-3 h-3 text-white" />}
              </div>
              <input type="checkbox" className="hidden" checked={behavioural.charmPricing} onChange={(e) => setFlag("charmPricing", e.target.checked)} />
              <div>
                <span className="font-medium text-gray-900 block">Charm Pricing</span>
                <span className="text-xs text-gray-500">End prices in 9 (e.g., R99 vs R100)</span>
              </div>
           </label>

           <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${behavioural.decoyEnabled ? 'border-indigo-500 bg-indigo-50' : 'border-gray-100 hover:border-gray-200'}`}>
              <div className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center ${behavioural.decoyEnabled ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'}`}>
                {behavioural.decoyEnabled && <Check className="w-3 h-3 text-white" />}
              </div>
              <input type="checkbox" className="hidden" checked={behavioural.decoyEnabled} onChange={(e) => setFlag("decoyEnabled", e.target.checked)} />
              <div>
                <span className="font-medium text-gray-900 block">Decoy Effect</span>
                <span className="text-xs text-gray-500">Add a less attractive option to boost others</span>
              </div>
           </label>
        </div>
      </div>

      {/* Tier Builder */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Pricing Tiers</h3>
          <button onClick={addTier} className="text-sm text-indigo-600 font-medium hover:text-indigo-700 flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add Tier
          </button>
        </div>

        <div className="space-y-3">
          {(behavioural.tiers || []).length === 0 ? (
            <p className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl">No tiers added. Add tiers to create Good/Better/Best options.</p>
          ) : (
            (behavioural.tiers || []).map((t) => (
              <div key={t.id} className="flex gap-3 items-center">
                <input
                  type="text"
                  value={t.name}
                  onChange={(e) => updateTier(t.id, "name", e.target.value)}
                  className="flex-grow px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 outline-none"
                  placeholder="Tier Name"
                />
                <div className="relative w-32">
                  <span className="absolute left-3 top-2.5 text-gray-400 text-sm">R</span>
                  <input
                    type="number"
                    value={t.price}
                    onChange={(e) => updateTier(t.id, "price", e.target.value)}
                    className="w-full pl-8 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 outline-none"
                  />
                </div>
                <button onClick={() => removeTier(t.id)} className="p-2 text-gray-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const Step6Models = ({ data, update }) => {
  const costData = data.costData || { unitCost: 0 };
  const value = data.valuePerception || { evc: 0, willingnessToPay: {} };
  const competition = data.competition || { referencePrice: 0, competitors: [] };
  const behavioural = data.behaviouralPricing || { charmPricing: true, tiers: [] };

  const compute = () => {
    const unit = safeNum(costData.unitCost);
    const costPlus = Math.round(unit * 1.3); // 30% markup default

    // value-based
    const evc = safeNum(value.evc);
    const wmin = safeNum(value.willingnessToPay?.min);
    const wmax = safeNum(value.willingnessToPay?.max);
    const valueBased = evc > 0 ? Math.round(evc * 0.6) : Math.round((wmin + wmax) / 2 || 0);

    // competition
    const prices = (competition.competitors || []).map((c) => safeNum(c.price)).filter((p) => p > 0);
    const competitionAligned = competition.referencePrice || (prices.length ? Math.round(prices.reduce((a,b)=>a+b,0)/prices.length) : 0);

    // behavioural
    let behaviouralPrice = 0;
    if (behavioural.tiers && behavioural.tiers.length) {
      const standard = behavioural.tiers.find((t) => /standard|recommended|best/i.test(t.name));
      if (standard) behaviouralPrice = safeNum(standard.price);
      else {
        const sorted = [...behavioural.tiers].sort((a, b) => a.price - b.price);
        behaviouralPrice = safeNum(sorted[Math.floor(sorted.length / 2)]?.price || 0);
      }
    } else {
      behaviouralPrice = behavioural.charmPricing ? makeCharm(Math.round(valueBased || costPlus)) : Math.round(valueBased || costPlus);
    }

    const values = [costPlus, valueBased, competitionAligned, behaviouralPrice].filter((v) => v > 0);
    const final = values.length ? Math.round(values.sort((a,b)=>a-b)[Math.floor(values.length/2)]) : Math.round(costPlus || 0);

    return { costPlus, valueBased, competitionAligned, behaviouralPrice, final };
  };

  const makeCharm = (n) => {
    if (n <= 10) return Math.max(1, n - 1);
    return n % 100 === 0 ? n - 1 : n; // Simple charm logic
  };

  const modelResults = useMemo(compute, [costData, value, competition, behavioural]);

  useEffect(() => {
    update("modelResults", modelResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelResults]);

  const ModelCard = ({ title, price, desc, active }) => (
    <div className={`p-5 rounded-2xl border transition-all ${active ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg scale-105' : 'bg-white border-gray-100 hover:border-indigo-200'}`}>
      <p className={`text-sm mb-1 ${active ? 'text-indigo-100' : 'text-gray-500'}`}>{title}</p>
      <p className="text-3xl font-bold mb-2">R {fmt(price)}</p>
      <p className={`text-xs ${active ? 'text-indigo-200' : 'text-gray-400'}`}>{desc}</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <ModelCard title="Cost Plus (30%)" price={modelResults.costPlus} desc="Safe floor price covering costs." />
        <ModelCard title="Value Based" price={modelResults.valueBased} desc="Based on customer ROI & WTP." />
        <ModelCard title="Competition" price={modelResults.competitionAligned} desc="Aligned with market average." />
      </div>

      <div className="mt-8">
        <div className="bg-gradient-to-br from-indigo-900 to-indigo-800 rounded-2xl p-8 text-center text-white shadow-xl">
          <h3 className="text-indigo-200 font-medium mb-2 uppercase tracking-wide text-sm">Recommended Price Point</h3>
          <div className="text-6xl font-extrabold mb-4">R {fmt(modelResults.final)}</div>
          <p className="text-indigo-200 max-w-md mx-auto text-sm">
            This price is calculated as the median of your models, balancing profitability, competitiveness, and customer value.
          </p>
        </div>
      </div>
    </div>
  );
};

const Step7Summary = ({ data, handleSave }) => {
  const { productData = {}, modelResults = {}, behaviouralPricing = {} } = data;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h2 className="font-bold text-gray-900 text-lg">{productData.name || "Untitled Product"}</h2>
            <p className="text-sm text-gray-500">Pricing Strategy Summary</p>
          </div>
          <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
             Ready
          </div>
        </div>
        
        <div className="p-6 text-center border-b border-gray-100">
          <p className="text-sm text-gray-500 mb-1">Final Recommended Price</p>
          <p className="text-5xl font-bold text-indigo-600">R {fmt(modelResults.final)}</p>
        </div>

        {behaviouralPricing.tiers?.length > 0 && (
          <div className="p-6 bg-gray-50/50">
             <h4 className="font-medium text-gray-900 mb-3 text-sm uppercase tracking-wide">Tiered Structure</h4>
             <div className="space-y-2">
                {behaviouralPricing.tiers.map(t => (
                  <div key={t.id} className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-100">
                    <span className="font-medium text-gray-700">{t.name}</span>
                    <span className="font-bold text-gray-900">R {fmt(t.price)}</span>
                  </div>
                ))}
             </div>
          </div>
        )}
      </div>

      <button
        onClick={handleSave}
        className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold text-lg hover:bg-indigo-700 hover:shadow-lg transition-all flex items-center justify-center gap-2"
      >
        <Save className="w-5 h-5" /> Save Pricing Model
      </button>
    </div>
  );
};

// ---------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------

const PricingTool = ({ bookId, onBack }) => {
  const toolSlug = "pricing-tool";

  const [view, setView] = useState(ViewState.DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // Load Data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await toolsService.getToolData(bookId, toolSlug);
        setItems(data || []);
      } catch (err) {
        console.error("Error loading pricing data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [bookId]);

  // Save Data
  const handleSave = async (item) => {
    try {
      const saved = await toolsService.saveToolData(bookId, toolSlug, item);
      setItems((prev) => {
        const exists = prev.some((i) => i.id === saved.id);
        return exists ? prev.map((i) => (i.id === saved.id ? saved : i)) : [saved, ...prev];
      });
      setSelectedItem(saved);
      setView(ViewState.MODEL_VIEW);
    } catch (err) {
      alert("Failed to save pricing model.");
    }
  };

  // Delete Data
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this pricing item?")) return;
    try {
      await toolsService.deleteToolData(bookId, toolSlug, id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setView(ViewState.DASHBOARD);
    } catch (err) {
      console.error("Error deleting item:", err);
    }
  };

  // -------------------------------------------------------
  // VIEWS
  // -------------------------------------------------------

  const Dashboard = () => {
    const avgPrice = items.length ? items.reduce((a, b) => a + (b.modelResults?.final || 0), 0) / items.length : 0;

    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-6xl mx-auto p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Pricing Strategy</h1>
                <p className="text-gray-500">Calculate and optimize your product pricing</p>
              </div>
            </div>
            <button
              onClick={() => { setSelectedItem(null); setView(ViewState.EDITOR); }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2 shadow-sm transition-all"
            >
              <Plus className="w-4 h-4" /> New Model
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={Calculator} label="Pricing Models" value={items.length} color="blue" />
            <StatCard icon={TrendingUp} label="Avg. Price Point" value={`R ${fmt(avgPrice)}`} color="emerald" />
            <StatCard icon={BarChart3} label="Active Sessions" value={items.length} subValue="Last updated today" color="indigo" />
          </div>

          {/* List */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
             <h2 className="font-semibold text-gray-900 mb-4">Saved Models</h2>
             {loading ? (
               <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
             ) : items.length === 0 ? (
               <EmptyState 
                 icon={Calculator} 
                 title="No pricing models yet" 
                 description="Create your first pricing model to start optimizing your revenue."
                 action={
                   <button onClick={() => { setSelectedItem(null); setView(ViewState.EDITOR); }} className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2 mt-4">
                     <Plus className="w-5 h-5" /> Create Model
                   </button>
                 }
               />
             ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 {items.map(item => (
                   <button 
                     key={item.id}
                     onClick={() => { setSelectedItem(item); setView(ViewState.MODEL_VIEW); }}
                     className="flex items-center justify-between p-5 bg-white rounded-xl border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all text-left group"
                   >
                     <div>
                       <h3 className="font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors">{item.productData?.name || "Unnamed Product"}</h3>
                       <p className="text-sm text-gray-500 mt-1">
                         {item.behaviouralPricing?.tiers?.length > 0 
                           ? `${item.behaviouralPricing.tiers.length} Tiers Defined` 
                           : "Single Price Point"}
                       </p>
                     </div>
                     <div className="text-right">
                       <p className="text-lg font-bold text-gray-900">R {fmt(item.modelResults?.final)}</p>
                       <span className="text-xs text-gray-400">Recommended</span>
                     </div>
                   </button>
                 ))}
               </div>
             )}
          </div>
        </div>
      </div>
    );
  };

  const Editor = () => {
    const [step, setStep] = useState(1);
    const [data, setData] = useState(selectedItem || {
      type: "session",
      productData: {},
      costData: {},
      valuePerception: {},
      competition: {},
      behaviouralPricing: {},
      modelResults: {},
      aiInsights: {}
    });

    const update = (field, value) => setData((prev) => ({ ...prev, [field]: value }));

    const steps = [
      { id: 1, label: "Basics", icon: ShoppingBag },
      { id: 2, label: "Costs", icon: Calculator },
      { id: 3, label: "Value", icon: Zap },
      { id: 4, label: "Market", icon: Users },
      { id: 5, label: "Psychology", icon: Lightbulb },
      { id: 6, label: "Models", icon: PieChart },
      { id: 7, label: "Review", icon: Check },
    ];

    return (
      <div className="h-full overflow-y-auto bg-gray-50/50">
        <div className="max-w-5xl mx-auto p-6 space-y-6">
          
          {/* Editor Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setView(ViewState.DASHBOARD)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">New Pricing Model</h1>
            </div>
            <div className="flex gap-2">
               {step > 1 && (
                 <button onClick={() => setStep(s => s - 1)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">Back</button>
               )}
               {step < 7 && (
                 <button onClick={() => setStep(s => s + 1)} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-medium">Next</button>
               )}
            </div>
          </div>

          {/* Stepper */}
          <div className="flex overflow-x-auto pb-2 gap-2 hide-scrollbar">
            {steps.map((s) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isCompleted = step > s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setStep(s.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    isActive 
                      ? "bg-indigo-600 text-white shadow-md" 
                      : isCompleted
                        ? "bg-indigo-50 text-indigo-700"
                        : "bg-white text-gray-500 border border-gray-100 hover:bg-gray-50"
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-white' : isCompleted ? 'text-indigo-600' : 'text-gray-400'}`} />
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Step Content */}
          <div className="min-h-[400px]">
            {step === 1 && <Step1Basics data={data} update={update} />}
            {step === 2 && <Step2Costs data={data} update={update} />}
            {step === 3 && <Step3Value data={data} update={update} />}
            {step === 4 && <Step4Competition data={data} update={update} />}
            {step === 5 && <Step5Behavioural data={data} update={update} />}
            {step === 6 && <Step6Models data={data} update={update} />}
            {step === 7 && <Step7Summary data={data} handleSave={() => handleSave(data)} />}
          </div>

        </div>
      </div>
    );
  };

  const ModelView = () => {
    if (!selectedItem) return null;
    const { productData, modelResults, behaviouralPricing } = selectedItem;

    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          <div className="flex items-center justify-between">
             <button onClick={() => setView(ViewState.DASHBOARD)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-3 py-2 hover:bg-gray-100 rounded-xl transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to List
             </button>
             <button onClick={() => handleDelete(selectedItem.id)} className="text-red-600 px-3 py-2 hover:bg-red-50 rounded-xl text-sm font-medium flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Delete
             </button>
          </div>

          <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm">
             <div className="bg-indigo-600 p-8 text-center text-white">
                <p className="text-indigo-200 text-sm uppercase tracking-wider font-medium mb-2">Final Recommended Price</p>
                <h1 className="text-6xl font-bold mb-4">R {fmt(modelResults?.final)}</h1>
                <p className="text-xl text-indigo-100">{productData?.name}</p>
             </div>
             
             <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                   <div className="p-4 bg-gray-50 rounded-2xl text-center">
                      <p className="text-xs text-gray-500 uppercase">Cost Plus</p>
                      <p className="font-bold text-gray-900 text-lg">R {fmt(modelResults?.costPlus)}</p>
                   </div>
                   <div className="p-4 bg-gray-50 rounded-2xl text-center">
                      <p className="text-xs text-gray-500 uppercase">Value Based</p>
                      <p className="font-bold text-gray-900 text-lg">R {fmt(modelResults?.valueBased)}</p>
                   </div>
                   <div className="p-4 bg-gray-50 rounded-2xl text-center">
                      <p className="text-xs text-gray-500 uppercase">Market</p>
                      <p className="font-bold text-gray-900 text-lg">R {fmt(modelResults?.competitionAligned)}</p>
                   </div>
                </div>

                {behaviouralPricing?.tiers?.length > 0 && (
                   <div>
                     <h3 className="font-bold text-gray-900 mb-4">Pricing Tiers</h3>
                     <div className="space-y-3">
                       {behaviouralPricing.tiers.map((t, i) => (
                         <div key={i} className="flex justify-between items-center p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors">
                           <span className="font-medium text-gray-700">{t.name}</span>
                           <span className="font-bold text-indigo-600 text-lg">R {fmt(t.price)}</span>
                         </div>
                       ))}
                     </div>
                   </div>
                )}
             </div>
             <div className="p-4 bg-gray-50 border-t border-gray-100 text-center">
                <button onClick={() => { setView(ViewState.EDITOR); }} className="text-indigo-600 font-medium text-sm hover:underline">
                  Edit Model
                </button>
             </div>
          </div>
        </div>
      </div>
    );
  };

  switch (view) {
    case ViewState.EDITOR: return <Editor />;
    case ViewState.MODEL_VIEW: return <ModelView />;
    default: return <Dashboard />;
  }
};

export default PricingTool;